import { GROUP_ROLES, type GroupRole, USER_GROUP_SORT_KEYS, type UserGroupListItem, type UserGroupSortKey } from "@seagames/contracts";
import { type ColumnDef, DataTable, Field, IconButton, Icons, type ListChip, ListHeader, ListToolbar, Select, useUiStrings } from "@seagames/ui";
import { Link, Outlet, createFileRoute, useParams } from "@tanstack/react-router";
import { useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { z } from "zod";
import { ConfirmDeleteModal } from "../../components/common/confirm-delete-modal";
import { EditDeleteActions } from "../../components/common/edit-delete-actions";
import { timestampColumn, usageColumn } from "../../components/common/list-columns";
import { type GroupFormTarget, GroupFormModal } from "../../components/user-groups/group-form-modal";
import { FiltersSheet } from "../../components/users/filters-sheet";
import { ActiveChip, FiltersButton, Tag } from "../../components/users/ui";
import { ApiError } from "../../lib/api/client";
import { requireAdminRole } from "../../lib/auth/guards";
import { listSearch } from "../../lib/list-page/list-search";
import { sortSelect } from "../../lib/list-page/sort-select";
import { useConfirmDelete } from "../../lib/list-page/use-confirm-delete";
import { useListPage } from "../../lib/list-page/use-list-page";
import { deleteUserGroup, invalidateUserGroups, userGroupsListOptions } from "../../lib/user-groups/queries";
import { roleLabel } from "../../lib/users/labels";

/** Users › User Groups. Reads are ADMIN / SUPER_ADMIN; only SUPER_ADMIN creates, edits and deletes. */
export const Route = createFileRoute("/_app/users/groups")({
	beforeLoad: ({ context }) => requireAdminRole(context.session),
	validateSearch: listSearch(USER_GROUP_SORT_KEYS, {
		role_code: z.enum(GROUP_ROLES),
		// A BOOLEAN here, not the "true"/"false" the API takes: TanStack Router JSON-parses
		// each value, so `?is_active=false` arrives as `false`. The string branch covers a hand-typed URL.
		is_active: z.union([z.boolean(), z.enum(["true", "false"]).transform((v) => v === "true")]),
	}),
	component: UserGroupsShell,
});

/** The group page ($groupId) nests under this route: when it matched, render it instead of the list. */
function UserGroupsShell() {
	const params = useParams({ strict: false });
	return params.groupId !== undefined ? <Outlet /> : <UserGroupsPage />;
}

/** The filters as the URL holds them. `""` = not set; the URL drops empties. */
interface GroupFilters extends Record<string, unknown> {
	search: string;
	role_code: "" | GroupRole;
	is_active: "" | "true" | "false";
}
const EMPTY_FILTERS: GroupFilters = { search: "", role_code: "", is_active: "" };
/** The Filters sheet's draft: the fields beyond the search box. */
type SheetDraft = Pick<GroupFilters, "role_code" | "is_active">;

function UserGroupsPage() {
	const { t } = useTranslation("userGroups");
	const { t: tUsers } = useTranslation(["users", "common"]);
	const { formatNumber } = useUiStrings();
	const { session } = Route.useRouteContext();
	const search = Route.useSearch();
	const navigate = Route.useNavigate();
	const isSuper = session.active_role === "SUPER_ADMIN";
	const [form, setForm] = useState<GroupFormTarget | null>(null);
	const [sheet, setSheet] = useState<SheetDraft | null>(null);

	// No default sort in the URL: the API orders by name when `sort` is undefined.
	const page = useListPage({
		search,
		navigate,
		listOptions: (q) =>
			userGroupsListOptions({
				...q,
				role_code: search.role_code,
				is_active: search.is_active === undefined ? undefined : search.is_active ? "true" : "false",
			}),
		columnsKey: "user-groups",
		filters: {
			empty: EMPTY_FILTERS,
			fromUrl: (s): GroupFilters => ({
				search: s.search ?? "",
				role_code: s.role_code ?? "",
				is_active: s.is_active === undefined ? "" : s.is_active ? "true" : "false",
			}),
			toUrl: (f) => ({
				search: f.search.trim() || undefined,
				role_code: f.role_code || undefined,
				is_active: f.is_active === "" ? undefined : f.is_active === "true",
			}),
		},
		summary: { range: (v) => t("pagination.summary", v), empty: t("pagination.empty") },
	});

	const total = page.list.data?.total;
	const isActiveValue: GroupFilters["is_active"] = search.is_active === undefined ? "" : search.is_active ? "true" : "false";
	const sheetCount = [search.role_code, isActiveValue].filter(Boolean).length;
	const openSheet = () => setSheet({ role_code: search.role_code ?? "", is_active: isActiveValue });
	const applySheet = () => {
		if (sheet) page.patch({ role_code: sheet.role_code || undefined, is_active: sheet.is_active === "" ? undefined : sheet.is_active === "true" });
		setSheet(null);
	};
	const resetSheet = () => {
		page.patch({ role_code: undefined, is_active: undefined });
		setSheet(null);
	};
	const sort = sortSelect<UserGroupSortKey>(
		search,
		"name:asc",
		[
			{ value: "name:asc", label: tUsers("common:sort.nameAsc") },
			{ value: "name:desc", label: tUsers("common:sort.nameDesc") },
			{ value: "created_at:desc", label: tUsers("common:sort.newest") },
			{ value: "created_at:asc", label: tUsers("common:sort.oldest") },
		],
		page.patch,
	);
	const chips: ListChip[] = [
		...(search.role_code
			? [{ kind: "toggle" as const, key: "role", label: roleLabel(tUsers, search.role_code), pressed: true, onChange: () => page.patch({ role_code: undefined }) }]
			: []),
		...(isActiveValue
			? [
					{
						kind: "toggle" as const,
						key: "status",
						label: isActiveValue === "true" ? t("filters.active") : t("filters.inactive"),
						pressed: true,
						onChange: () => page.patch({ is_active: undefined }),
					},
				]
			: []),
	];

	const del = useConfirmDelete<UserGroupListItem>({
		remove: async (r) => {
			try {
				await deleteUserGroup(r.user_group_id);
			} catch (err) {
				// 409 = it still has members (someone joined since this page loaded): say so in our words.
				if (err instanceof ApiError && err.code === "CONFLICT") throw new Error(t("errors.hasMembers"));
				throw err;
			}
		},
		invalidate: invalidateUserGroups,
		deleted: (r) => t("delete.deleted", { name: r.name }),
	});

	const columns: ColumnDef<UserGroupListItem, UserGroupSortKey>[] = [
		{
			key: "name",
			header: t("table.name"),
			sortable: true,
			lockVisible: true,
			minWidth: "14rem",
			cell: (r) => (
				<div className="flex min-w-0 flex-col gap-[0.125rem]">
					<Link
						to="/users/groups/$groupId"
						params={{ groupId: r.user_group_id }}
						search={{}}
						className="truncate text-[0.84375rem] leading-[1.25] font-semibold text-gms-navy-ink hover:text-gms-navy hover:underline"
					>
						{r.name}
					</Link>
					{r.description && (
						<span className="max-w-[32rem] truncate text-[0.75rem] leading-[1.3] text-gms-ink-3" title={r.description}>
							{r.description}
						</span>
					)}
				</div>
			),
		},
		{
			key: "role_code",
			header: t("table.role"),
			sortable: true,
			minWidth: "8rem",
			cell: (r) => <Tag>{roleLabel(tUsers, r.role_code)}</Tag>,
		},
		{
			key: "is_active" as UserGroupSortKey,
			header: t("table.status"),
			width: "7rem",
			cell: (r) => <ActiveChip active={r.is_active}>{r.is_active ? t("table.active") : t("table.inactive")}</ActiveChip>,
		},
		{ ...usageColumn<UserGroupListItem, UserGroupSortKey>("member_count", t("table.members"), t("table.membersTitle"), "6.5rem"), sortable: true },
		usageColumn("scope_count", t("table.scopes"), t("table.scopesTitle"), "6.5rem"),
		usageColumn("permission_count", t("table.permissions"), t("table.permissionsTitle"), "7rem"),
		usageColumn("sport_count", t("table.sports"), t("table.sportsTitle"), "6rem"),
		timestampColumn("updated_at", t("table.updated"), true),
	];

	return (
		<div className="flex min-w-0 flex-col gap-[1.125rem]">
			<ListHeader
				title={t("page.title")}
				count={total === undefined ? undefined : t("page.count", { count: total, n: formatNumber(total) })}
				description={<Trans t={t} i18nKey="page.intro" components={{ strong: <strong className="font-semibold text-gms-ink-2" /> }} />}
				actionLabel={t("page.addGroup")}
				onAction={isSuper ? () => setForm({ mode: "create" }) : undefined}
			/>

			<ListToolbar
				search={search.search ?? ""}
				onSearch={(v) => page.patch({ search: v || undefined })}
				searchPlaceholder={t("filters.searchLabel")}
				afterSearch={<FiltersButton label={t("filters.button")} count={sheetCount} onClick={openSheet} />}
				sort={sort}
				chips={chips}
				onClearAll={page.clearFilters}
			/>

			<DataTable<UserGroupListItem, UserGroupSortKey>
				{...page.table}
				variant="list"
				columns={columns}
				rowKey={(r) => r.user_group_id}
				empty={page.emptyState({
					filtered: { title: t("empty.filteredTitle"), description: t("empty.filteredDescription") },
					none: { title: t("empty.noneTitle"), description: t("empty.noneDescription") },
					clearLabel: t("empty.clearFilters"),
				})}
				rowActions={(r) => (
					<EditDeleteActions
						deleteLabel={t("table.deleteLabel", { name: r.name })}
						onDelete={isSuper ? () => del.ask(r) : undefined}
						canDelete={r.member_count === 0}
						variant="list"
					>
						{/* The group page is read-only for ADMIN, so everyone gets the eye. */}
						<IconButton
							label={t("table.viewLabel", { name: r.name })}
							icon={<Icons.Eye />}
							variant="row"
							onClick={() => void navigate({ to: "/users/groups/$groupId", params: { groupId: r.user_group_id }, search: {} })}
						/>
					</EditDeleteActions>
				)}
			/>

			<FiltersSheet open={sheet !== null} onClose={() => setSheet(null)} title={t("filters.sheetTitle")} onApply={applySheet} onReset={resetSheet}>
				<Field label={t("filters.roleLabel")} htmlFor="f-group-role">
					<Select
						id="f-group-role"
						options={GROUP_ROLES.map((r) => ({ value: r, label: roleLabel(tUsers, r) }))}
						value={sheet?.role_code || null}
						onChange={(v) => setSheet((d) => d && { ...d, role_code: (v ?? "") as GroupFilters["role_code"] })}
						placeholder={t("filters.allRoles")}
						searchable={false}
						clearable
					/>
				</Field>
				<Field label={t("filters.statusLabel")} htmlFor="f-group-status">
					<Select
						id="f-group-status"
						options={[
							{ value: "true", label: t("filters.active") },
							{ value: "false", label: t("filters.inactive") },
						]}
						value={sheet?.is_active || null}
						onChange={(v) => setSheet((d) => d && { ...d, is_active: (v ?? "") as GroupFilters["is_active"] })}
						placeholder={t("filters.anyStatus")}
						searchable={false}
						clearable
					/>
				</Field>
			</FiltersSheet>

			{isSuper && <GroupFormModal target={form} onClose={() => setForm(null)} />}
			{isSuper && (
				<ConfirmDeleteModal
					{...del.modal}
					title={t("delete.title")}
					description={<Trans t={t} i18nKey="delete.description" values={{ name: del.row?.name ?? "" }} components={{ strong: <strong className="font-semibold text-base-content" /> }} />}
					confirmLabel={t("delete.confirm")}
					errorPrefix={t("delete.failed")}
				/>
			)}
		</div>
	);
}
